import React from 'react';
import { IoShareOutline, IoCloseOutline, IoDownloadOutline } from 'react-icons/io5';
import { usePWAInstall } from '@/hooks/usePWAInstall';

/**
 * iOS Instructions Modal
 */
function IOSInstructionsModal({ onDismiss }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl animate-slide-up">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Install ZAPT</h3>
            <button 
              onClick={onDismiss}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <IoCloseOutline className="w-5 h-5" />
            </button>
          </div>
          
          <p className="text-gray-600 mb-6">
            Install ZAPT on your iPhone or iPad for the best experience:
          </p>
          
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium">
                1
              </span>
              <div className="pt-0.5">
                <span className="text-gray-700">Tap the </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-gray-700">
                  <IoShareOutline className="w-4 h-4 text-blue-500" />
                  Share
                </span>
                <span className="text-gray-700"> button in Safari</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium">
                2
              </span>
              <div className="pt-0.5">
                <span className="text-gray-700">Scroll down and tap </span>
                <span className="font-medium text-gray-900">"Add to Home Screen"</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium">
                3
              </span>
              <div className="pt-0.5">
                <span className="text-gray-700">Tap </span>
                <span className="font-medium text-gray-900">"Add"</span>
                <span className="text-gray-700"> in the top right</span>
              </div>
            </li>
          </ol>
          
          <button
            onClick={onDismiss}
            className="w-full mt-6 px-4 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer"
          >
            Got it
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

/**
 * PWA Install Banner
 * Shows a dismissible banner prompting users to install the app
 */
export default function PWAInstallBanner() {
  const {
    isInstallable,
    isInstalled,
    isIOS,
    showIOSInstructions,
    promptInstall,
    dismissIOSInstructions,
    dismissInstallBanner,
  } = usePWAInstall();

  // Don't show if already installed or not installable
  if (isInstalled || !isInstallable) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40 safe-area-pb">
        <div className="mx-4 mb-4 sm:mx-auto sm:max-w-md">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                  <img 
                    src="/pwa-192x192.png" 
                    alt="ZAPT" 
                    className="w-10 h-10 rounded-lg"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">Install ZAPT</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Add to your home screen for quick access
                  </p>
                </div>
                <button
                  onClick={dismissInstallBanner}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  aria-label="Dismiss"
                >
                  <IoCloseOutline className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={dismissInstallBanner}
                  className="flex-1 px-4 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Not now
                </button>
                <button
                  onClick={promptInstall}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isIOS ? (
                    <>
                      <IoShareOutline className="w-4 h-4" />
                      Install
                    </>
                  ) : (
                    <>
                      <IoDownloadOutline className="w-4 h-4" />
                      Install
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <IOSInstructionsModal onDismiss={dismissIOSInstructions} />
      )}
      
      <style>{`
        .safe-area-pb {
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
      `}</style>
    </>
  );
}
